import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
  useEffect,
} from "react";

// Interface du contexte
export interface WorkorderContextType {
  workorder: EasyTech.Workorder;
  updateWorkorder: (updatedWorkorder: EasyTech.Workorder) => void;

  // Fonctions utilitaires pour accéder aux données organisées
  getMachines: () => EasyTech.WorkorderMachine[];
  getWorkorderHoses: () => EasyTech.WorkorderHose[];
  getWorkorderParts: () => EasyTech.WoPart[];
  getMachineParts: (machineId: string) => EasyTech.WoPart[];

  // Fonctions pour ajouter/modifier des éléments
  addMachine: (machine: EasyTech.WorkorderMachine) => void;
  addHoseToMachine: (machineId: string, hose: EasyTech.WorkorderHose) => void;
  addHoseToWorkorder: (hose: EasyTech.WorkorderHose) => void;
  addPartToMachine: (machineId: string, part: EasyTech.WoPart) => void;
  addPartToHose: (hoseId: string, part: EasyTech.WoPart) => void;
  addPartToWorkorder: (part: EasyTech.WoPart) => void;
  updateMachine: (
    machineId: string,
    updatedMachine: EasyTech.WorkorderMachine
  ) => void;

  // Fonctions pour supprimer des éléments
  removeMachine: (machineId: string) => void;
  removeHose: (hoseId: string) => void;
  removePart: (partId: string) => void;

  // Méthodes pour les hoses
  addHose: (hose: EasyTech.WorkorderHose) => void;
  updateHose: (hoseId: string, updatedHose: EasyTech.WorkorderHose) => void;

  // Méthode pour mettre à jour une pièce
  updatePart: (partId: string, part: EasyTech.WoPart) => void;

  // Méthodes pour supprimer des pièces
  removePartFromHose: (hoseId: string, partId: string) => void;
  removePartFromWorkorder: (partId: string) => void;
  removePartFromMachine: (machineId: string, partId: string) => void;

  // Ajouter les éléments que tu souhaites globaliser
  machines: any[];
  machinesCustomer: any[];
  workorderId: string;
  onOpenMachineDialog: () => void;
  onMachineUpdated: (machine: any) => void;
  inlineAdd: boolean;
  setInlineAdd: (inlineAdd: boolean) => void;
  
  // ID du contexte d'édition actuel pour gérer les parts drafts par instance
  currentEditContext: string | null;
  setCurrentEditContext: (context: string | null) => void;

  // ID de la part actuellement en cours d'édition (pour éviter les conflits avec l'ajout inline)
  editingPartId: string | null;
  setEditingPartId: (id: string | null) => void;
}

// Exporter le contexte
export const WorkorderContext = createContext<WorkorderContextType | undefined>(
  undefined
);

// Props du provider
interface WorkorderProviderProps {
  value: EasyTech.Workorder;
  children: ReactNode;
  setWorkorderState?: React.Dispatch<React.SetStateAction<EasyTech.Workorder | null>>;
}

// Provider du contexte
export const WorkorderProvider = ({
  children,
  value,
  setWorkorderState,
}: WorkorderProviderProps) => {
  const [workorder, setWorkorder] = useState<EasyTech.Workorder>(value);
  const machines: EasyTech.WorkorderMachine[] = [];
  const machinesCustomer: EasyTech.Machine[] = [];

  //inlineAdd
  const [inlineAdd, setInlineAdd] = useState(false);
  
  // État pour suivre quel composant est en cours d'édition
  const [currentEditContext, setCurrentEditContext] = useState<string | null>(null);

  // État pour suivre quelle part est en cours d'édition
  const [editingPartId, setEditingPartId] = useState<string | null>(null);

  // AJOUT: Synchroniser l'état interne avec la prop value
  useEffect(() => {
    setWorkorder(value);
  }, [value]);

  // Mise à jour du workorder complet
  const updateWorkorder = (updatedWorkorder: EasyTech.Workorder) => {
    setWorkorder(updatedWorkorder);
    if (setWorkorderState) {
      setWorkorderState(updatedWorkorder);
    }
  };

  // Fonctions utilitaires pour accéder aux données - maintenant directement disponibles
  const getMachines = () => {
    const machines = workorder?.machines || [];
    // Trier les machines par ID pour avoir toujours le même ordre
    // Vérifier que les IDs existent avant de les comparer
    return [...machines].sort((a, b) => {
      if (!a.id && !b.id) return 0;
      if (!a.id) return -1;
      if (!b.id) return 1;
      return a.id.localeCompare(b.id);
    });
  };
  
  const getWorkorderHoses = () => {
    const hoses = workorder?.hoses || [];
    // Trier les hoses par ID pour avoir toujours le même ordre
    // Vérifier que les IDs existent avant de les comparer
    return [...hoses].sort((a, b) => {
      if (!a.id && !b.id) return 0;
      if (!a.id) return -1;
      if (!b.id) return 1;
      return a.id.localeCompare(b.id);
    });
  };
  
  const getWorkorderParts = () => {
    const parts = workorder?.parts || [];
    // Trier les parts par ID pour avoir toujours le même ordre
    // Vérifier que les IDs existent avant de les comparer
    return [...parts].sort((a, b) => {
      if (!a.id && !b.id) return 0;
      if (!a.id) return -1;
      if (!b.id) return 1;
      return a.id.localeCompare(b.id);
    });
  };

  const getMachineParts = (machineId: string) => {
    const machine = workorder?.machines.find(m => m.id === machineId);
    
    // S'assurer que le tableau parts existe
    const parts = machine?.parts || [];
    
    // Filtrer pour n'avoir que les parts directement liées à la machine (pas aux hoses)
    const machineParts = parts.filter(part => !part.hose);
    
    // Trier les parts par ID pour avoir toujours le même ordre
    // Vérifier que les IDs existent avant de les comparer
    return [...machineParts].sort((a, b) => {
      if (!a.id && !b.id) return 0;
      if (!a.id) return -1;
      if (!b.id) return 1;
      return a.id.localeCompare(b.id);
    });
  };

  // Ajouter une machine
  const addMachine = (machine: EasyTech.WorkorderMachine) => {
    // S'assurer que la machine a un tableau hoses initialisé
    const machineWithHoses = {
      ...machine,
      hoses: machine.hoses || [],
      parts: machine.parts || []
    };
    
    setWorkorder((prev) => ({
      ...prev,
      machines: [...prev.machines, machineWithHoses],
    }));
  };

  // Ajouter un hose
  const addHose = (hose: EasyTech.WorkorderHose) => {
    const hoseWithParts = {
      ...hose,
      parts: hose.parts || []
    };
    
    setWorkorder((prev) => {
      // Vérifier si le hose existe déjà dans le workorder
      const hoseExists = prev.hoses.some((h) => h.id === hoseWithParts.id);
    
      // Si le hose existe déjà, ne pas le rajouter
      if (hoseExists) {
        return prev;
      }
      
      // Si le hose est lié à une machine, l'ajouter à la machine
      if (hoseWithParts.machine && hoseWithParts.machine.id) {
        const machineIndex = prev.machines.findIndex(
          (m) => m.id === hoseWithParts.machine?.id
        );
        
        if (machineIndex !== -1) {
          // Vérifier si le hose existe déjà dans la machine
          const hoseExistsInMachine = prev.machines[machineIndex].hoses.some(
            (h) => h.id === hoseWithParts.id
          );
          
          if (hoseExistsInMachine) {
            // console.log("Le hose existe déjà dans la machine, pas d'ajout");
            return prev;
          }
          
          // Ajouter le hose à la machine
          const updatedMachines = [...prev.machines];
          updatedMachines[machineIndex] = {
            ...updatedMachines[machineIndex],
            hoses: [...updatedMachines[machineIndex].hoses, hoseWithParts],
          };
          
          return { ...prev, machines: updatedMachines };
        }
      }
      
      // Sinon, l'ajouter directement au workorder
      return { ...prev, hoses: [...prev.hoses, hoseWithParts] };
    });
  };

  // Ajouter un hose à une machine
  const addHoseToMachine = (machineId: string, hose: EasyTech.WorkorderHose) => {
    setWorkorder((prev) => {
      const machineIndex = prev.machines.findIndex((m) => m.id === machineId);
      
      if (machineIndex === -1) {
        // console.log(`Machine ${machineId} non trouvée, impossible d'ajouter le hose`);
        return prev;
      }
      
      // Vérifier si le hose existe déjà dans la machine
      const hoseExistsInMachine = prev.machines[machineIndex].hoses.some(
        (h) => h.id === hose.id
      );
      
      if (hoseExistsInMachine) {
        // console.log("Le hose existe déjà dans la machine, pas d'ajout");
        return prev;
      }
      
      // Ajouter le hose à la machine
      const updatedMachines = [...prev.machines];
      updatedMachines[machineIndex] = {
        ...updatedMachines[machineIndex],
        hoses: [...updatedMachines[machineIndex].hoses, hose],
      };
      
      return { ...prev, machines: updatedMachines };
    });
  };

  // Ajouter un hose au workorder
  const addHoseToWorkorder = (hose: EasyTech.WorkorderHose) => {
    // S'assurer que le hose a un tableau parts initialisé
    const hoseWithParts = {
      ...hose,
      parts: hose.parts || []
    };
    
    setWorkorder((prev) => ({
      ...prev,
      hoses: [...prev.hoses, hoseWithParts],
    }));
  };

  // Ajouter une pièce à une machine
  const addPartToMachine = (machineId: string, part: EasyTech.WoPart) => {
    setWorkorder((prev) => {
      const machines = prev.machines.map((machine) => {
        if (machine.id === machineId) {
          // S'assurer que le tableau parts existe et est initialisé
          const currentParts = Array.isArray(machine.parts) ? machine.parts : [];
          
          // Vérifier si la pièce existe déjà pour éviter les doublons
          const partExists = currentParts.some(p => p.id === part.id);
          
          if (partExists) {
            // Si la pièce existe déjà, remplacer par la nouvelle version
            return {
              ...machine,
              parts: currentParts.map(p => p.id === part.id ? part : p)
            };
          } else {
            // Sinon, ajouter la nouvelle pièce
            return {
              ...machine,
              parts: [...currentParts, part],
            };
          }
        }
        return machine;
      });
      return {
        ...prev,
        machines,
      };
    });
  };

  // Ajouter une pièce à un hose
  const addPartToHose = (hoseId: string, part: EasyTech.WoPart) => {
    setWorkorder(prev => {
      // Créer une nouvelle référence d'objet pour garantir la détection du changement
      const updatedWorkorder = JSON.parse(JSON.stringify(prev));
      let found = false;
      
      // Mettre à jour dans les machines
      updatedWorkorder.machines = updatedWorkorder.machines.map((machine: any) => {
        const hoseIndex = machine.hoses.findIndex((h: any) => h.id === hoseId);
        if (hoseIndex !== -1) {
          found = true;
          // S'assurer que le tableau parts existe
          if (!Array.isArray(machine.hoses[hoseIndex].parts)) {
            machine.hoses[hoseIndex].parts = [];
          }
          
          // Vérifier si la part existe déjà
          const partExists = machine.hoses[hoseIndex].parts.some((p: any) => p.id === part.id);
          
          // Si elle n'existe pas, l'ajouter
          if (!partExists) {
            machine.hoses[hoseIndex].parts.push(part);
          }
        }
        return machine;
      });

      // Si non trouvé, mettre à jour dans les hoses du workorder
      if (!found) {
        updatedWorkorder.hoses = updatedWorkorder.hoses.map((hose: any) => {
          if (hose.id === hoseId) {
            // S'assurer que le tableau parts existe
            if (!Array.isArray(hose.parts)) {
              hose.parts = [];
            }
            
            // Vérifier si la part existe déjà
            const partExists = hose.parts.some((p: any) => p.id === part.id);
            
            // Si elle n'existe pas, l'ajouter
            if (!partExists) {
              hose.parts.push(part);
            }
          }
          return hose;
        });
      }
      
      return updatedWorkorder;
    });
  };

  // Ajouter une pièce au workorder
  const addPartToWorkorder = (part: EasyTech.WoPart) => {
    setWorkorder((prev) => ({
      ...prev,
      parts: [...prev.parts, part],
    }));
  };

  // Supprimer une machine
  const removeMachine = (machineId: string) => {
    setWorkorder((prev) => ({
      ...prev,
      machines: prev.machines.filter((machine) => machine.id !== machineId),
    }));
  };

  // Supprimer un hose
  const removeHose = (hoseId: string) => {
    // Vérifier si le hose est rattaché à une machine
    let found = false;
    
    setWorkorder((prev) => {
      // Chercher et supprimer dans les machines
      const updatedMachines = prev.machines.map(machine => {
        const hoseExists = machine.hoses.some(h => h.id === hoseId);
        if (hoseExists) {
          found = true;
          return {
            ...machine,
            hoses: machine.hoses.filter(h => h.id !== hoseId)
          };
        }
        return machine;
      });
      
      if (found) {
        return { ...prev, machines: updatedMachines };
      }
      
      // Si non trouvé dans les machines, supprimer des hoses du workorder
      return {
        ...prev,
        hoses: prev.hoses.filter(h => h.id !== hoseId)
      };
    });
  };

  // Supprimer une pièce
  const removePart = (partId: string) => {
    const updatedWorkorder = { ...workorder };
    let partFound = false;

    // Vérifier dans les machines et leurs hoses
    updatedWorkorder.machines = workorder.machines.map((machine) => {
      // Vérifier les pièces de la machine
      const machineParts = machine.parts.filter((part) => {
        if (part.id === partId) {
          partFound = true;
          return false;
        }
        return true;
      });

      // Vérifier dans les hoses de la machine
      const machineHoses = machine.hoses.map((hose) => {
        return {
          ...hose,
          parts: hose.parts.filter((part) => {
            if (part.id === partId) {
              partFound = true;
              return false;
            }
            return true;
          }),
        };
      });

      return {
        ...machine,
        parts: machineParts,
        hoses: machineHoses,
      };
    });

    // Si la pièce n'a pas été trouvée, vérifier dans les hoses du workorder
    if (!partFound) {
      updatedWorkorder.hoses = workorder.hoses.map((hose) => {
        return {
          ...hose,
          parts: hose.parts.filter((part) => {
            if (part.id === partId) {
              partFound = true;
              return false;
            }
            return true;
          }),
        };
      });
    }

    // Si la pièce n'a toujours pas été trouvée, vérifier dans les pièces du workorder
    if (!partFound) {
      updatedWorkorder.parts = workorder.parts.filter(
        (part) => part.id !== partId
      );
    }

    setWorkorder(updatedWorkorder);
  };

  const updateMachine = useCallback(
    (machineId: string, updatedMachine: EasyTech.WorkorderMachine) => {
      setWorkorder((prev) => {
        // Find la machine existante pour préserver ses parts et hoses
        const existingMachine = prev.machines.find(m => m.id === machineId);
        
        // Si la machine existe, => mets à jour la machine et les notes avec les nouvelles valeurs 
        if (existingMachine) {
          return {
            ...prev,
            machines: prev.machines.map((m) => {
              if (m.id === machineId) {
                // Garder les parts et hoses existants
                return {
                  ...existingMachine,           // Conserve toutes les propriétés existantes (y compris parts et hoses)
                  machine: updatedMachine.machine, // Met à jour l'objet machine
                  notes: updatedMachine.notes     // Met à jour les notes
                };
              }
              return m;
            }),
          };
        }
        
        // Si la machine n'existe pas peu probable (il n y a pas de machine dans l'ui normalement), => garder le comportement par défaut
        return {
          ...prev,
          machines: prev.machines.map((m) =>
            m.id === machineId ? updatedMachine : m
          ),
        };
      });
    },
    []
  );

  const updateHose = useCallback(
    (hoseId: string, updatedHose: EasyTech.WorkorderHose) => {
      setWorkorder((prev) => ({
        ...prev,
        hoses: (prev.hoses || []).map((h) =>
          h.id === hoseId ? updatedHose : h
        ),
      }));
    },
    []
  );

  const updatePart = useCallback(
    (partId: string, updatedPart: EasyTech.WoPart) => {
      setWorkorder((prev) => {
        const updatedWorkorder = { ...prev };
        let partFound = false;

        // Vérifier dans les machines et leurs hoses
        updatedWorkorder.machines = prev.machines.map((machine) => {
          // Vérifier les pièces de la machine
          const machineParts = machine.parts.map((part) => {
            if (part.id === partId) {
              partFound = true;
              return { ...part, ...updatedPart };
            }
            return part;
          });

          // Vérifier dans les hoses de la machine
          const machineHoses = machine.hoses.map((hose) => {
            return {
              ...hose,
              parts: hose.parts.map((part) => {
                if (part.id === partId) {
                  partFound = true;
                  return { ...part, ...updatedPart };
                }
                return part;
              }),
            };
          });

          return {
            ...machine,
            parts: machineParts,
            hoses: machineHoses,
          };
        });

        // Si la pièce n'a pas été trouvée, vérifier dans les hoses du workorder
        if (!partFound) {
          updatedWorkorder.hoses = prev.hoses.map((hose) => {
            return {
              ...hose,
              parts: hose.parts.map((part) => {
                if (part.id === partId) {
                  partFound = true;
                  return { ...part, ...updatedPart };
                }
                return part;
              }),
            };
          });
        }

        // Si la pièce n'a toujours pas été trouvée, vérifier dans les pièces du workorder
        if (!partFound) {
          updatedWorkorder.parts = prev.parts.map((part) =>
            part.id === partId ? { ...part, ...updatedPart } : part
          );
        }

        return updatedWorkorder;
      });
    },
    []
  );

  // Supprimer une pièce d'un hose
  const removePartFromHose = (hoseId: string, partId: string) => {
    const updatedWorkorder = { ...workorder };
    let hoseFound = false;

    // Vérifier dans les machines
    updatedWorkorder.machines = workorder.machines.map((machine) => {
      const machineHoses = machine.hoses.map((hose) => {
        if (hose.id === hoseId) {
          hoseFound = true;
          return {
            ...hose,
            parts: (hose.parts || []).filter(part => part.id !== partId),
          };
        }
        return hose;
      });

      return {
        ...machine,
        hoses: machineHoses,
      };
    });

    // Si le hose n'a pas été trouvé dans les machines, chercher dans les hoses du workorder
    if (!hoseFound) {
      updatedWorkorder.hoses = workorder.hoses.map((hose) => {
        if (hose.id === hoseId) {
          return {
            ...hose,
            parts: (hose.parts || []).filter(part => part.id !== partId),
          };
        }
        return hose;
      });
    }

    setWorkorder(updatedWorkorder);
  };

  // Supprimer une pièce du workorder
  const removePartFromWorkorder = (partId: string) => {
    setWorkorder((prev) => ({
      ...prev,
      parts: prev.parts.filter((part) => part.id !== partId),
    }));
  };

  // Ajout de la fonction removePartFromMachine
  const removePartFromMachine = (machineId: string, partId: string) => {
    setWorkorder((prev) => ({
      ...prev,
      machines: prev.machines.map((machine) =>
        machine.id === machineId
          ? { ...machine, parts: machine.parts.filter((part) => part.id !== partId) }
          : machine
      ),
    }));
  };

  // Fonctions de gestion des machines
  const handleOpenMachineDialog = () => {
    // implémentation
  };
  
  const handleMachineUpdated = (machine: any) => {
    // implémentation
  };

  const contextValue: WorkorderContextType = {
    workorder,
    updateWorkorder,
    getMachines,
    getWorkorderHoses,
    getWorkorderParts,
    getMachineParts,
    addMachine,
    addHoseToMachine,
    addHoseToWorkorder,
    addPartToMachine,
    addPartToHose,
    addPartToWorkorder,
    updateMachine,
    removeMachine,
    removeHose,
    removePart,
    addHose,
    updateHose,
    updatePart,
    removePartFromHose,
    removePartFromWorkorder,
    removePartFromMachine,
    machines,
    machinesCustomer,
    workorderId: workorder?.id || "",
    onOpenMachineDialog: handleOpenMachineDialog,
    onMachineUpdated: handleMachineUpdated,
    inlineAdd,
    setInlineAdd,
    currentEditContext,
    setCurrentEditContext,
    editingPartId,
    setEditingPartId
  };

  return (
    <WorkorderContext.Provider value={contextValue}>
      {children}
    </WorkorderContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte
export const useWorkorderContext = () => {
  const context = useContext(WorkorderContext);

  if (context === undefined) {
    throw new Error(
      "useWorkorderContext doit être utilisé à l'intérieur d'un WorkorderProvider"
    );
  }

  return context;
};
